/* ============================================================
   Monmouth Cards — Cart JS
   assets/monmouth-cart.js
   ============================================================
   Handles: cart drawer open/close, AJAX qty updates,
   remove items, discount code, upsell add-to-cart,
   free shipping bar, cart count badge.
   ============================================================ */
(function () {
  'use strict';

  /* Real fix (cart-js-double-inclusion-prompt.md) — this file was
     confirmed loading twice on the real cart page (once sitewide via
     theme.liquid, once redundantly via sections/monmouth-cart.liquid),
     and the two copies didn't even execute back-to-back since one was
     deferred and one wasn't. That's fixed at the source (the redundant
     inclusion is removed), but this guard stays as a defensive safety
     net: if this file is ever accidentally included twice again by this
     or any other section, the second execution is a safe no-op instead
     of silently double-binding every event listener in here (cart
     drawer open/close, qty +/-, remove, upsell add-to-cart, quick-add).
     Confirmed live during that bug: view_cart fired twice and the
     Remove button behaved inconsistently — exactly what this prevents. */
  if (window.__mcCartInit) return;
  window.__mcCartInit = true;

  /* Free-ship threshold comes from theme setting via data attribute
     (cart drawer or cart page). Falls back to $199 if not present. */
  function getFreeShipCents() {
    var el = document.querySelector('[data-free-ship-cents]');
    var v = el && parseInt(el.getAttribute('data-free-ship-cents'), 10);
    return (v && !isNaN(v)) ? v : 19900;
  }
  var FREE_SHIP_CENTS = getFreeShipCents(); // set from theme setting

  /* ── helpers ── */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }

  /* Real race-condition fix (referral-code-race-condition-add-to-cart-
     prompt.md) — reads the mc_ref_code cookie set by layout/theme.liquid
     and returns it as a cart-attributes fragment to merge into a /cart/
     add.js call, so the code lands in the SAME request that adds the
     item instead of racing a separate background sync call. */
  function mcGetReferralAttribute() {
    var m = document.cookie.match(/(?:^|; )mc_ref_code=([^;]*)/);
    return m ? { mc_ref_code: decodeURIComponent(m[1]) } : {};
  }
  function money(cents) { return '$' + (cents / 100).toFixed(2); }

  /* ══════════════════════════════════════════════
     CART DRAWER
  ══════════════════════════════════════════════ */
  function initCartDrawer() {
    var drawer  = $('#mc-cart-drawer');
    var overlay = $('#mc-cart-overlay');
    var closeBtn = $('#mc-cart-drawer-close');
    var viewFull = $('#mc-cart-view-full');

    if (!drawer) return;

    function open() {
      fetchCart(renderDrawer);
      drawer.classList.add('mc-cart-drawer--active');
      overlay.classList.add('mc-cart-overlay--active');
      document.body.style.overflow = 'hidden';
      drawer.setAttribute('aria-hidden', 'false');
      /* Real fix (ga4-analytics-tracking-prompt.md part 3) — view_cart, real fires when the drawer opens. */
      if (window.mcTrack && typeof window.mcTrack.viewCart === 'function') window.mcTrack.viewCart();
    }

    function close() {
      drawer.classList.remove('mc-cart-drawer--active');
      overlay.classList.remove('mc-cart-overlay--active');
      document.body.style.overflow = '';
      drawer.setAttribute('aria-hidden', 'true');
    }

    /* Trigger: cart icon in nav uses data-cart-trigger */
    $$('[data-cart-trigger]').forEach(function (btn) {
      btn.addEventListener('click', function (e) { e.preventDefault(); open(); });
    });

    if (closeBtn) closeBtn.addEventListener('click', close);
    if (overlay)  overlay.addEventListener('click', close);
    if (viewFull) viewFull.addEventListener('click', function () {
      close();
      window.location.href = '/cart';
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('mc-cart-drawer--active')) close();
    });

    /* Re-render drawer when external "add to cart" fires */
    document.addEventListener('mc:cart-updated', function () {
      syncCartCount();
      if (drawer.classList.contains('mc-cart-drawer--active')) {
        fetchCart(renderDrawer);
      }
    });

    /* Open drawer from PDP add-to-cart */
    document.addEventListener('mc:open-cart-drawer', function () {
      open();
    });

    /* Discount code in drawer */
    initDiscount(
      '#mc-drawer-discount-input',
      '#mc-drawer-discount-apply',
      '#mc-drawer-discount-msg',
      '#mc-cart-checkout-link'
    );
  }

  /* ── fetch /cart.js ── */
  function fetchCart(cb) {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(cb)
      .catch(function (err) { console.warn('MC Cart fetch error', err); });
  }

  /* ── render drawer with cart data ── */
  function renderDrawer(cart) {
    var countEl  = $('#mc-cart-drawer-count');
    var itemsEl  = $('#mc-cart-drawer-items');
    var footEl   = $('#mc-cart-drawer-foot');

    if (countEl) countEl.textContent = '(' + cart.item_count + ')';
    syncCartCount(cart.item_count);
    updateFreeShipBar(cart.total_price, '#mc-drawer-freeship-text', '#mc-drawer-freeship-fill');

    if (!itemsEl) return;

    if (cart.item_count === 0) {
      itemsEl.innerHTML =
        '<div class="mc-cart-empty">' +
          '<div class="mc-cart-empty__icon">🛒</div>' +
          '<div class="mc-cart-empty__text">Your cart is empty</div>' +
          '<a href="/collections/all" class="mc-cart-empty__btn">Start Shopping</a>' +
        '</div>';
      if (footEl) footEl.style.display = 'none';
      clearRecommendations('mc-cart-drawer-upsells');
      return;
    }

    if (footEl) footEl.style.display = '';

    /* Items */
    itemsEl.innerHTML = cart.items.map(function (item) {
      var img = item.image
        ? '<img src="' + item.image + '" alt="" loading="lazy">'
        : '<span class="mc-cart-item__img-placeholder">📦</span>';

      var variant = (item.variant_title && item.variant_title !== 'Default Title')
        ? '<div class="mc-cart-item__variant">' + item.variant_title + '</div>'
        : '';

      return (
        '<div class="mc-cart-item mc-cart-item--compact" data-key="' + item.key + '" data-track-product-id="' + item.product_id + '" data-track-title="' + escHtml(item.product_title) + '" data-track-price="' + (item.price / 100) + '" data-track-qty="' + item.quantity + '">' +
          '<div class="mc-cart-item__img">' + img + '</div>' +
          '<div class="mc-cart-item__info">' +
            '<a href="' + item.url + '" class="mc-cart-item__title">' + escHtml(item.product_title) + '</a>' +
            variant +
            '<div class="mc-cart-item__actions">' +
              '<div class="mc-cart-item__qty">' +
                '<button class="mc-cart-item__qty-btn" data-key="' + item.key + '" data-action="decrease" aria-label="Decrease quantity">−</button>' +
                '<span class="mc-cart-item__qty-num">' + item.quantity + '</span>' +
                '<button class="mc-cart-item__qty-btn" data-key="' + item.key + '" data-action="increase" aria-label="Increase quantity">+</button>' +
              '</div>' +
              '<button class="mc-cart-item__remove" data-key="' + item.key + '">Remove</button>' +
            '</div>' +
          '</div>' +
          '<div class="mc-cart-item__price">' + money(item.line_price) + '</div>' +
        '</div>'
      );
    }).join('');

    /* Bind item actions */
    bindItemActions(itemsEl, function (key, qty) {
      changeLineItem(key, qty, function (updated) {
        renderDrawer(updated);
      });
    });

    /* Totals */
    updateDrawerTotals(cart);

    /* "You Might Also Like" — anchored on the most-recently-added item */
    fetchAndRenderRecommendations(cart, 'mc-cart-drawer-upsells', 'drawer');
  }

  function updateDrawerTotals(cart) {
    var totalsEl    = $('#mc-cart-drawer-totals');
    var checkoutBtn = $('#mc-cart-checkout-link');

    if (totalsEl) {
      var freeShip = cart.total_price >= FREE_SHIP_CENTS;
      totalsEl.innerHTML =
        '<div class="mc-cart-drawer__total-row">' +
          '<span>Subtotal</span><span>' + money(cart.total_price) + '</span>' +
        '</div>' +
        '<div class="mc-cart-drawer__total-row' + (freeShip ? ' mc-cart-drawer__total-row--free' : '') + '">' +
          '<span>Shipping</span>' +
          '<span>' + (freeShip ? '<span style="color:#1B7A3D;font-weight:700">FREE</span>' : '$7.99') + '</span>' +
        '</div>';
    }

    if (checkoutBtn) {
      checkoutBtn.textContent = 'Checkout — ' + money(cart.total_price);
    }
  }

  /* ══════════════════════════════════════════════
     FREE SHIPPING BAR
  ══════════════════════════════════════════════ */
  function updateFreeShipBar(totalCents, textSel, fillSel) {
    var textEl = $(textSel);
    var fillEl = $(fillSel);
    if (!textEl && !fillEl) return;

    var pct = Math.min((totalCents / FREE_SHIP_CENTS) * 100, 100);
    if (fillEl) fillEl.style.width = pct + '%';

    if (textEl) {
      if (totalCents >= FREE_SHIP_CENTS) {
        textEl.className = 'mc-free-ship__text mc-free-ship__text--unlocked';
        textEl.innerHTML = '✅ You\'ve unlocked <strong>FREE shipping!</strong>';
      } else {
        var remaining = money(FREE_SHIP_CENTS - totalCents);
        textEl.className = 'mc-free-ship__text';
        textEl.innerHTML =
          '🚚 Add <strong class="mc-free-ship__amount">' + remaining + '</strong> more for ' +
          '<strong class="mc-free-ship__label">FREE shipping</strong>';
      }
    }
  }

  /* ══════════════════════════════════════════════
     FULL CART PAGE
  ══════════════════════════════════════════════ */
  function initCartPage() {
    var page = $('.mc-cart-page');
    if (!page) return;

    /* Real fix (ga4-analytics-tracking-prompt.md part 3) — view_cart, real fires when the real cart page loads (the drawer's own open() covers the drawer surface separately). */
    if (window.mcTrack && typeof window.mcTrack.viewCart === 'function') window.mcTrack.viewCart();

    /* Read server-rendered cart total for free-ship bar */
    var totalCents = parseInt(page.dataset.cartTotal || '0', 10);
    updateFreeShipBar(totalCents, '#mc-page-freeship-text', '#mc-page-freeship-fill');

    /* Bind qty/remove on all page items */
    bindItemActions(page, function (key, qty, itemEl) {
      changeLineItem(key, qty, function (cart) {
        if (qty === 0 && itemEl) {
          slideOut(itemEl, function () {
            itemEl.remove();
            if (cart.item_count === 0) setTimeout(function () { window.location.reload(); }, 200);
          });
        } else if (itemEl) {
          var priceEl = itemEl.querySelector('.mc-cart-item__price');
          var qtyEl   = itemEl.querySelector('.mc-cart-item__qty-num');
          var updated = cart.items.find(function (i) { return i.key === key; });
          if (priceEl && updated) priceEl.textContent = money(updated.line_price);
          if (qtyEl) qtyEl.textContent = qty;
        }

        syncCartCount(cart.item_count);
        updatePageTotals(cart);
        updateFreeShipBar(cart.total_price, '#mc-page-freeship-text', '#mc-page-freeship-fill');

        var countEl = $('#mc-page-item-count');
        if (countEl) countEl.textContent = cart.item_count + ' ' + (cart.item_count === 1 ? 'item' : 'items') + ' in your cart';

        /* Real fix (2026-08-13) — this page's qty/remove flow never fired
           mc:cart-updated, so nothing on it (including the new
           recommendations row) ever knew the cart had changed. Dispatching
           it here is also harmless for the drawer's own listener (a no-op
           when the drawer isn't open). */
        document.dispatchEvent(new CustomEvent('mc:cart-updated'));
      });
    });

    /* Discount code on cart page */
    initDiscount(
      '#mc-page-discount-input',
      '#mc-page-discount-apply',
      '#mc-page-discount-msg',
      '#mc-page-checkout-btn'
    );

    /* "You Might Also Like" — initial render on load, refreshed on every
       mc:cart-updated (including the dispatch just added above, and the
       existing dispatch already fired by initUpsells()'s add-to-cart). */
    fetchCart(function (cart) { fetchAndRenderRecommendations(cart, 'mc-cart-page-upsells', 'page'); });
    document.addEventListener('mc:cart-updated', function () {
      fetchCart(function (cart) { fetchAndRenderRecommendations(cart, 'mc-cart-page-upsells', 'page'); });
    });
  }

  function updatePageTotals(cart) {
    var subtotalEl  = $('#mc-page-subtotal');
    var shippingEl  = $('#mc-page-shipping');
    var totalEl     = $('#mc-page-total');

    if (subtotalEl) subtotalEl.textContent = money(cart.total_price);
    if (totalEl)    totalEl.textContent    = money(cart.total_price);
    if (shippingEl) {
      if (cart.total_price >= FREE_SHIP_CENTS) {
        shippingEl.innerHTML = '<span style="color:#1B7A3D;font-weight:700">FREE</span>';
      } else {
        shippingEl.textContent = '$7.99';
      }
    }
  }

  /* ══════════════════════════════════════════════
     LOADING STATE HELPERS
  ══════════════════════════════════════════════ */
  function setItemLoading(itemEl, loading) {
    if (!itemEl) return;
    if (loading) {
      itemEl.classList.add('mc-cart-item--loading');
      itemEl.querySelectorAll('button').forEach(function (b) { b.disabled = true; });
    } else {
      itemEl.classList.remove('mc-cart-item--loading');
      itemEl.querySelectorAll('button').forEach(function (b) { b.disabled = false; });
    }
  }

  /* ══════════════════════════════════════════════
     SHARED: BIND ITEM ACTIONS (qty +/- and remove)
  ══════════════════════════════════════════════ */
  function bindItemActions(ctx, onChange) {
    /* Use event delegation so it works after AJAX re-renders */
    ctx.addEventListener('click', function (e) {
      /* Qty buttons */
      var qtyBtn = e.target.closest('.mc-cart-item__qty-btn');
      if (qtyBtn && ctx.contains(qtyBtn) && !qtyBtn.disabled) {
        var key     = qtyBtn.dataset.key;
        var action  = qtyBtn.dataset.action;
        var itemEl  = qtyBtn.closest('.mc-cart-item');
        var qtyEl   = itemEl && itemEl.querySelector('.mc-cart-item__qty-num');
        var current = qtyEl ? parseInt(qtyEl.textContent, 10) : 1;
        var newQty  = action === 'increase' ? current + 1 : Math.max(0, current - 1);
        /* Real fix (ga4-analytics-tracking-prompt.md part 3) — the "-"
           button dropping quantity to 0 is a real removal too, same as
           the explicit Remove button below. */
        if (newQty === 0 && itemEl && window.mcTrack && typeof window.mcTrack.removeFromCart === 'function') {
          window.mcTrack.removeFromCart({
            id: itemEl.dataset.trackProductId,
            title: itemEl.dataset.trackTitle,
            price: parseFloat(itemEl.dataset.trackPrice) || 0,
            quantity: parseInt(itemEl.dataset.trackQty, 10) || 1
          });
        }
        setItemLoading(itemEl, true);
        onChange(key, newQty, itemEl);
        return;
      }

      /* Remove buttons */
      var removeBtn = e.target.closest('.mc-cart-item__remove');
      if (removeBtn && ctx.contains(removeBtn) && !removeBtn.disabled) {
        var itemEl2 = removeBtn.closest('.mc-cart-item');
        /* Real fix (ga4-analytics-tracking-prompt.md part 3) — read the
           real item's data from its own element BEFORE it's removed
           (shared between the drawer's JS-rendered items and the cart
           page's server-rendered ones — both carry the same
           data-track-* attributes). */
        if (itemEl2 && window.mcTrack && typeof window.mcTrack.removeFromCart === 'function') {
          window.mcTrack.removeFromCart({
            id: itemEl2.dataset.trackProductId,
            title: itemEl2.dataset.trackTitle,
            price: parseFloat(itemEl2.dataset.trackPrice) || 0,
            quantity: parseInt(itemEl2.dataset.trackQty, 10) || 1
          });
        }
        setItemLoading(itemEl2, true);
        onChange(removeBtn.dataset.key, 0, itemEl2);
      }
    });
  }

  /* ══════════════════════════════════════════════
     AJAX: CHANGE LINE ITEM
  ══════════════════════════════════════════════ */
  function changeLineItem(key, qty, cb) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    })
      .then(function (r) {
        if (!r.ok) {
          /* Shopify rejected the change (stale key, already removed, etc.)
             Re-fetch the real cart state and pass that to the callback instead */
          return fetch('/cart.js').then(function (r2) { return r2.json(); });
        }
        return r.json();
      })
      .then(cb)
      .catch(function (err) { console.warn('MC cart change error', err); });
  }

  /* ══════════════════════════════════════════════
     "YOU MIGHT ALSO LIKE" — automatic recommendations
     Real fix (2026-08-13) — Shopify's native, zero-setup /recommendations/
     products.json endpoint (`related` intent, algorithmic, no manual
     curation). Anchored on whichever cart item was most recently added
     (the last entry in cart.items — matches add order for genuinely new
     lines; an existing line whose quantity was just bumped stays in place,
     but that item is excluded from its own recommendations anyway by the
     already-in-cart filter below, so the fallback is still a valid
     "recommendations minus what's already in the cart" result either way).
     Deliberately separate from initUpsells() below, which is the CURATED
     "Frequently Added Together" mechanism (a fixed collection) — this one
     is fully automatic and has no admin-configurable collection at all.
     Both reuse the same [data-upsell-variant] add-to-cart click pattern
     (bound once, globally, in initUpsells()) so recommendation cards work
     without any extra event binding here.
  ══════════════════════════════════════════════ */
  function clearRecommendations(containerId) {
    var el = document.getElementById(containerId);
    if (el) el.innerHTML = '';
  }

  /* Drawer: small horizontal-scroll cards (image + name side by side).
     Page: wide grid cards (image on top, full width). Deliberately
     different DOM shapes, not just a class swap — see
     cart-page-recommendations-layout-fix-prompt.md. */
  function renderUpsellCardHtml(p, context) {
    var variants = p.variants || [];
    var variant = variants.filter(function (v) { return v.available; })[0] || variants[0];
    if (!variant) return '';
    var img = p.featured_image || (p.images && p.images[0]) || '';

    if (context === 'page') {
      /* Reuse the site's standard product card (snippets/monmouth-product-card.liquid)
         instead of a bespoke design — same classes, styled by
         monmouth-product-card.css (already loaded sitewide). Field names below
         are verified against a live /recommendations/products.json response,
         not guessed: prices are raw cents on both product.price and
         variant.price/variant.compare_at_price; featured_image is a plain CDN
         URL string (or null); url is already a full relative path with the
         recommendation-tracking query string built in. See
         cart-recommendations-card-format-prompt.md. */
      var titleEsc = escHtml(p.title);
      var isSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      return (
        '<div class="mc-product-card">' +
          '<a href="' + p.url + '" class="mc-product-card__img-link">' +
            '<div class="mc-product-card__img">' +
              (img
                ? '<img src="' + img + '" alt="' + titleEsc + '" width="200" height="280" loading="lazy" class="mc-product-card__image">'
                : '') +
            '</div>' +
          '</a>' +
          '<div class="mc-product-card__body">' +
            '<a href="' + p.url + '" class="mc-product-card__title" title="' + titleEsc + '">' + titleEsc + '</a>' +
            '<div class="mc-product-card__price' + (isSale ? ' mc-product-card__price--sale' : '') + '">' +
              '<span class="mc-product-card__price-current">' + money(variant.price) + '</span>' +
              (isSale ? '<span class="mc-product-card__price-compare">' + money(variant.compare_at_price) + '</span>' : '') +
            '</div>' +
            '<button class="mc-product-card__btn' + (variant.available ? '' : ' mc-product-card__btn--soldout') + '" data-upsell-variant="' + variant.id + '"' +
              (variant.available ? '' : ' disabled') + '>' +
              (variant.available ? 'Add to Cart' : 'Sold Out') +
            '</button>' +
          '</div>' +
        '</div>'
      );
    }

    return (
      '<div class="mc-cart-upsell-card">' +
        '<div class="mc-cart-upsell-card__top">' +
          '<div class="mc-cart-upsell-card__img">' +
            (img ? '<img src="' + img + '" alt="" loading="lazy">' : '') +
          '</div>' +
          '<div class="mc-cart-upsell-card__name">' + escHtml(p.title) + '</div>' +
        '</div>' +
        '<div class="mc-cart-upsell-card__bottom">' +
          '<span class="mc-cart-upsell-card__price">' + money(variant.price) + '</span>' +
          '<button class="mc-cart-upsell-card__btn" data-upsell-variant="' + variant.id + '"' +
            (variant.available ? '' : ' disabled') + '>' +
            (variant.available ? 'Add' : 'Sold Out') +
          '</button>' +
        '</div>' +
      '</div>'
    );
  }

  function renderRecommendationsHtml(products, context) {
    var cards = products.map(function (p) { return renderUpsellCardHtml(p, context); }).filter(Boolean);

    if (!cards.length) return '';

    if (context === 'page') {
      return (
        '<div class="mc-cart-page-upsells__title">You Might Also Like</div>' +
        '<div class="mc-cart-page-upsells__row">' + cards.join('') + '</div>'
      );
    }

    return (
      '<div class="mc-cart-upsells">' +
        '<div class="mc-cart-upsells__title">You Might Also Like</div>' +
        '<div class="mc-cart-upsells__row">' + cards.join('') + '</div>' +
      '</div>'
    );
  }

  function fetchAndRenderRecommendations(cart, containerId, context) {
    if (!document.getElementById(containerId)) return;

    if (!cart.items.length) {
      clearRecommendations(containerId);
      return;
    }

    /* Anchor on whichever product was most recently added. cart.items
       order is not reliably append-order on this store (confirmed live —
       a fresh add can land at index 0), so we track the last-added
       variant id explicitly (set by every /cart/add.js call site) and
       fall back to the last array item only if that lookup fails. */
    var anchor = null;
    try {
      var lastVariantId = sessionStorage.getItem('mc_last_added_variant_id');
      if (lastVariantId) {
        anchor = cart.items.filter(function (i) {
          return String(i.variant_id) === String(lastVariantId);
        })[0] || null;
      }
    } catch (e) { /* sessionStorage unavailable */ }
    if (!anchor) anchor = cart.items[cart.items.length - 1];
    var cartProductIds = cart.items.map(function (i) { return i.product_id; });

    var displayLimit = (context === 'page') ? 5 : 4;
    fetch('/recommendations/products.json?product_id=' + encodeURIComponent(anchor.product_id) + '&limit=10&intent=related')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        var products = (data && data.products) || [];
        var filtered = products
          .filter(function (p) { return cartProductIds.indexOf(p.id) === -1; })
          .slice(0, displayLimit);

        var el = document.getElementById(containerId);
        if (!el) return; /* container may have been removed by a later re-render */

        if (!filtered.length) {
          el.innerHTML = '';
          return;
        }
        el.innerHTML = renderRecommendationsHtml(filtered, context);
      })
      .catch(function () {
        /* Fail quietly — no empty state, no error message (per spec) */
        clearRecommendations(containerId);
      });
  }

  /* ══════════════════════════════════════════════
     UPSELL: ADD TO CART
  ══════════════════════════════════════════════ */
  /* ══════════════════════════════════════════════
     PRODUCT-CARD QUICK ADD (collection/related grids)
     Real fix (ga4-analytics-tracking-prompt.md part 4) — this used to be
     a plain, non-JS form POST to /cart/add with no AJAX response to hook
     tracking into at all (a full-page redirect to the cart). Delegated
     so it works for every real monmouth-product-card.liquid instance
     sitewide, including ones rendered after this script runs (related
     products, infinite-scroll collection grids).
  ══════════════════════════════════════════════ */
  function initProductCardQuickAdd() {
    document.addEventListener('submit', function (e) {
      var form = e.target.closest && e.target.closest('.mc-product-card__quick-add-form');
      if (!form) return;
      e.preventDefault();

      var idInput = form.querySelector('[name="id"]');
      var btn = form.querySelector('button[type="submit"]');
      if (!idInput || !btn) return;

      var original = btn.textContent;
      btn.textContent = 'Adding…';
      btn.disabled = true;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: idInput.value, quantity: 1 })
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data.status) {
            btn.textContent = data.description || 'Error';
            btn.disabled = false;
            setTimeout(function () { btn.textContent = original; }, 2000);
            return;
          }
          btn.textContent = '✓ Added!';
          try { sessionStorage.setItem('mc_last_added_variant_id', String(data.variant_id || idInput.value)); } catch (e2) { /* no-op */ }

          try {
            if (window.mcTrack && typeof window.mcTrack.addToCart === 'function') {
              window.mcTrack.addToCart({
                id: data.product_id || idInput.value,
                title: data.product_title || data.title,
                price: (typeof data.price === 'number' ? data.price / 100 : undefined)
              }, 1);
            }
          } catch (e2) { /* no-op */ }

          document.dispatchEvent(new CustomEvent('mc:cart-updated'));
          document.dispatchEvent(new CustomEvent('mc:open-cart-drawer'));
          setTimeout(function () {
            btn.textContent = original;
            btn.disabled = false;
          }, 1600);
        })
        .catch(function () {
          btn.textContent = 'Error';
          btn.disabled = false;
          setTimeout(function () { btn.textContent = original; }, 2000);
        });
    });
  }

  function initUpsells() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-upsell-variant]');
      if (!btn) return;

      var variantId = btn.dataset.upsellVariant;
      var original  = btn.textContent;
      btn.textContent = '…';
      btn.disabled = true;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1, attributes: mcGetReferralAttribute() })
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          btn.textContent = '✓ Added';
          try { sessionStorage.setItem('mc_last_added_variant_id', String((data && data.variant_id) || variantId)); } catch (e) { /* no-op */ }

          /* Real fix (ga4-analytics-tracking-prompt.md part 4) — this real, working AJAX add-to-cart path (cart-drawer upsells) never called mcTrack.addToCart at all. */
          try {
            if (window.mcTrack && typeof window.mcTrack.addToCart === 'function' && data) {
              window.mcTrack.addToCart({
                id: data.product_id || variantId,
                title: data.product_title || data.title,
                price: (typeof data.price === 'number' ? data.price / 100 : undefined)
              }, 1);
            }
          } catch (e) { /* no-op */ }

          syncCartCount();
          document.dispatchEvent(new CustomEvent('mc:cart-updated'));
        })
        .catch(function () {
          btn.textContent = original;
          btn.disabled = false;
        });
    });
  }

  /* ══════════════════════════════════════════════
     DISCOUNT CODE
     Discount codes can't be applied via AJAX in Shopify.
     We capture the code and redirect via /discount/{CODE}
  ══════════════════════════════════════════════ */
  function initDiscount(inputSel, applySel, msgSel, checkoutSel) {
    var inputEl    = $(inputSel);
    var applyBtn   = $(applySel);
    var msgEl      = $(msgSel);
    var checkoutEl = $(checkoutSel);

    if (!inputEl || !applyBtn) return;

    /* Restore saved code */
    var savedCode = sessionStorage.getItem('mc_discount_code');
    if (savedCode) {
      inputEl.value = savedCode;
      showDiscountSaved(msgEl, checkoutEl, savedCode);
    }

    applyBtn.addEventListener('click', function () {
      var code = inputEl.value.trim().toUpperCase();
      if (!code) return;
      sessionStorage.setItem('mc_discount_code', code);
      showDiscountSaved(msgEl, checkoutEl, code);
    });

    inputEl.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') applyBtn.click();
    });
  }

  function showDiscountSaved(msgEl, checkoutEl, code) {
    if (msgEl) {
      msgEl.style.display = 'block';
      msgEl.className = msgEl.className.replace(/mc-cart-(summary__|)discount-msg--(success|note)/g, '').trim();
      msgEl.classList.add(
        msgEl.id && msgEl.id.includes('drawer')
          ? 'mc-cart-discount__msg--success'
          : 'mc-cart-summary__discount-msg--success'
      );
      msgEl.innerHTML = '✅ <strong>' + escHtml(code) + '</strong> saved — applies at checkout';
    }
    /* Update checkout href to apply discount via /discount/{code}?redirect=/checkout */
    if (checkoutEl) {
      checkoutEl.setAttribute('href', '/discount/' + encodeURIComponent(code) + '?redirect=/checkout');
    }
  }

  /* ══════════════════════════════════════════════
     CART COUNT BADGE
  ══════════════════════════════════════════════ */
  function syncCartCount(count) {
    if (count !== undefined) {
      $$('[data-cart-count]').forEach(function (el) {
        el.textContent = count;
        el.style.display = count > 0 ? '' : 'none';
      });
      return;
    }
    fetchCart(function (cart) { syncCartCount(cart.item_count); });
  }

  /* ══════════════════════════════════════════════
     UTILS
  ══════════════════════════════════════════════ */
  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function slideOut(el, cb) {
    el.style.overflow = 'hidden';
    el.style.transition = 'opacity 0.18s, height 0.28s ease, padding 0.28s ease, margin 0.28s ease';
    el.style.height = el.offsetHeight + 'px';
    el.style.opacity = '1';
    requestAnimationFrame(function () {
      el.style.height = '0';
      el.style.paddingTop = '0';
      el.style.paddingBottom = '0';
      el.style.marginTop = '0';
      el.style.marginBottom = '0';
      el.style.opacity = '0';
    });
    setTimeout(cb, 300);
  }

  /* ══════════════════════════════════════════════
     INIT
  ══════════════════════════════════════════════ */
  function init() {
    initCartDrawer();
    initCartPage();
    initUpsells();
    initProductCardQuickAdd();
  }

  /* Global helpers for Liquid-rendered cart items that use inline onclick */
  window.mcCartQty = function (key, qty) {
    var itemEl = document.querySelector('.mc-cart-item[data-key="' + key + '"]');
    setItemLoading(itemEl, true);
    changeLineItem(key, qty, function (cart) {
      if (qty === 0 && itemEl) {
        slideOut(itemEl, function () {
          itemEl.remove();
          if (cart.item_count === 0) setTimeout(function () { window.location.reload(); }, 200);
        });
      } else if (itemEl) {
        var qtyEl   = itemEl.querySelector('.mc-cart-qty__val');
        var priceEl = itemEl.querySelector('.mc-cart-item__price');
        var updated = cart.items.find(function (i) { return i.key === key; });
        if (qtyEl) qtyEl.textContent = qty;
        if (priceEl && updated) priceEl.textContent = money(updated.line_price);
        var dec = itemEl.querySelector('.mc-cart-qty__btn');
        var btns = itemEl.querySelectorAll('.mc-cart-qty__btn');
        if (btns[0]) btns[0].setAttribute('onclick', 'mcCartQty(\'' + key + '\',' + (qty - 1) + ')');
        if (btns[1]) btns[1].setAttribute('onclick', 'mcCartQty(\'' + key + '\',' + (qty + 1) + ')');
      }
      syncCartCount(cart.item_count);
      updatePageTotals(cart);
      updateFreeShipBar(cart.total_price, '#mc-page-freeship-text', '#mc-page-freeship-fill');
      var countEl = $('#mc-page-item-count');
      if (countEl) countEl.textContent = cart.item_count + ' ' + (cart.item_count === 1 ? 'item' : 'items') + ' in your cart';
    });
  };

  window.mcCartRemove = function (key) {
    window.mcCartQty(key, 0);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
